// Based on https://medium.com/the-non-traditional-developer/how-to-use-an-intersectionobserver-in-a-react-hook-9fb061ac6cb5
import {useEffect, useRef, type MutableRefObject, type DependencyList} from 'react';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {useMemoizedCallback} from './use-memoized-callback';

/**
 * Observes an element with an `IntersectionObserver` and invokes the callback
 * whenever its intersection with the viewport changes.
 *
 * @param callback - The `IntersectionObserver` callback to run on intersection changes.
 * @param element - A ref to the element to observe.
 * @param threshold - The intersection ratio at which the callback fires.
 * @param deps - Dependencies that, when changed, recreate the observer callback.
 *
 * @example
 * const ref = useRef<HTMLDivElement | null>(null);
 * useIntersect(([entry]) => { if (entry.isIntersecting) loadMore(); }, ref, 1, [loadMore]);
 */
export const useIntersect = <T extends IntersectionObserverCallback>(
    callback: T,
    element: MutableRefObject<Element | null>,
    threshold: number,
    deps: DependencyList,
) => {
    const observer = useRef<IntersectionObserver | null>(null);

    const memoizedCallback = useMemoizedCallback(callback, deps);

    useEffect(() => {
        if (hasValue(observer.current)) {
            observer.current.disconnect();
        }

        if (element.current) {
            observer.current = new window.IntersectionObserver(memoizedCallback, {
                root: null,
                rootMargin: '0px',
                threshold,
            });
            observer.current.observe(element.current);
        }

        return () => {
            if (hasValue(observer.current)) {
                observer.current.disconnect();
            }
        };
    }, [memoizedCallback, element, threshold]);
};
